/**
 * Welle 12 (pre134): Direkter P2P-Transport des Aufzeichnungspakets vom A/V-Gerät des
 * Beraters zum Companion (Terminal-Server), damit die Datei — wie Scan/Signatur/NFC — auf
 * dem TS landet. Eine EIGENE RTCPeerConnection zwischen den beiden Berater-Sitzungen; das
 * Signaling läuft huckepack über den bestehenden remote:cmd/remote:state-Relay (user-Room),
 * das Paket selbst gechunkt über einen DataChannel. Das Paket verlässt damit NIE den Server
 * (auch nicht verschlüsselt) — sauberste Privacy-Variante.
 */
import { loadIceServers } from './iceServers';

export const REC_CHUNK_SIZE = 16 * 1024;
const BUFFER_THRESH = 256 * 1024;

export interface RecSignal { type: 'offer' | 'answer' | 'ice'; sdp?: any; candidate?: any }
export interface RecMeta { fileName: string; size: number; mime: string; rootHash?: string; callId?: string }

/**
 * A/V-Seite: sendet das Paket. `start()` liefert true erst nach Empfangs-Quittung des
 * Companions, false bei Timeout (kein Companion da) oder Verbindungsfehler → Aufrufer fällt
 * dann auf die normale lokale Auslieferung zurück.
 */
export class RecordingSender {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private gotAnswer = false;
  private settled = false;
  private done: ((ok: boolean) => void) | null = null;

  constructor(private sendSignal: (s: RecSignal) => void) {}

  async start(blob: Blob, meta: RecMeta, opts: { answerTimeoutMs?: number } = {}): Promise<boolean> {
    const finished = new Promise<boolean>((res) => { this.done = res; });
    try {
      const iceServers = await loadIceServers();
      const pc = new RTCPeerConnection({ iceServers });
      this.pc = pc;
      pc.onicecandidate = (e) => { if (e.candidate) this.sendSignal({ type: 'ice', candidate: e.candidate.toJSON() }); };
      pc.onconnectionstatechange = () => {
        if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) this.settle(false);
      };
      const dc = pc.createDataChannel('bamoa-rec', { ordered: true });
      this.dc = dc;
      dc.binaryType = 'arraybuffer';
      dc.onmessage = (ev) => {
        if (typeof ev.data !== 'string') return;
        try { const m = JSON.parse(ev.data); if (m?.type === 'rec:ack') this.settle(m.ok !== false); } catch { /* */ }
      };
      dc.onopen = () => { void this.pump(blob, meta); };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.sendSignal({ type: 'offer', sdp: pc.localDescription });
    } catch {
      this.settle(false);
    }
    const timer = setTimeout(() => { if (!this.gotAnswer) this.settle(false); }, opts.answerTimeoutMs ?? 5000);
    const ok = await finished;
    clearTimeout(timer);
    this.cleanup();
    return ok;
  }

  async onSignal(s: RecSignal): Promise<void> {
    const pc = this.pc;
    if (!pc) return;
    try {
      if (s.type === 'answer' && s.sdp) { this.gotAnswer = true; await pc.setRemoteDescription(s.sdp); }
      else if (s.type === 'ice' && s.candidate) { await pc.addIceCandidate(s.candidate); }
    } catch { /* */ }
  }

  private async pump(blob: Blob, meta: RecMeta): Promise<void> {
    const dc = this.dc;
    if (!dc) return;
    try {
      const buf = new Uint8Array(await blob.arrayBuffer());
      const total = buf.byteLength;
      const totalChunks = Math.ceil(total / REC_CHUNK_SIZE) || 0;
      dc.send(JSON.stringify({ type: 'rec:start', fileName: meta.fileName, size: total, mime: meta.mime, rootHash: meta.rootHash, callId: meta.callId, totalChunks, chunkSize: REC_CHUNK_SIZE }));
      for (let i = 0; i < totalChunks; i++) {
        const s = i * REC_CHUNK_SIZE;
        const e = Math.min(s + REC_CHUNK_SIZE, total);
        let waited = 0;
        while (dc.bufferedAmount > BUFFER_THRESH) {
          await new Promise((r) => setTimeout(r, 8));
          if (dc.readyState !== 'open') { this.settle(false); return; }
          if ((waited += 8) > 60000) { this.settle(false); return; }
        }
        dc.send(buf.slice(s, e).buffer);
      }
      dc.send(JSON.stringify({ type: 'rec:end' }));
      // Quittung kommt per onmessage (rec:ack) → settle(true)
    } catch {
      this.settle(false);
    }
  }

  abort(): void { this.settle(false); this.cleanup(); }

  private settle(ok: boolean): void {
    if (this.settled) return;
    this.settled = true;
    this.done?.(ok);
  }

  private cleanup(): void {
    try { this.dc?.close(); } catch { /* */ }
    try { this.pc?.close(); } catch { /* */ }
    this.pc = null; this.dc = null;
  }
}

/**
 * Companion-Seite: empfängt das Paket. Reagiert auf eine eingehende `offer`, baut die
 * Antwort/ICE, sammelt die Chunks und ruft `onComplete(blob, meta)` auf.
 */
export class RecordingReceiver {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private meta: RecMeta | null = null;
  private chunks: ArrayBuffer[] = [];
  private received = 0;

  constructor(
    private sendSignal: (s: RecSignal) => void,
    private onComplete: (blob: Blob, meta: RecMeta) => void,
    private onProgress?: (pct: number) => void,
  ) {}

  async onSignal(s: RecSignal): Promise<void> {
    try {
      if (s.type === 'offer' && s.sdp) {
        const iceServers = await loadIceServers();
        const pc = new RTCPeerConnection({ iceServers });
        this.pc = pc;
        pc.onicecandidate = (e) => { if (e.candidate) this.sendSignal({ type: 'ice', candidate: e.candidate.toJSON() }); };
        pc.ondatachannel = (ev) => this.bind(ev.channel);
        await pc.setRemoteDescription(s.sdp);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.sendSignal({ type: 'answer', sdp: pc.localDescription });
      } else if (s.type === 'ice' && s.candidate && this.pc) {
        await this.pc.addIceCandidate(s.candidate);
      }
    } catch { /* */ }
  }

  private bind(dc: RTCDataChannel): void {
    this.dc = dc;
    dc.binaryType = 'arraybuffer';
    dc.onmessage = async (ev) => {
      if (typeof ev.data === 'string') {
        try {
          const m = JSON.parse(ev.data);
          if (m.type === 'rec:start') {
            this.meta = { fileName: m.fileName, size: m.size, mime: m.mime, rootHash: m.rootHash, callId: m.callId };
            this.chunks = []; this.received = 0;
            this.onProgress?.(0);
          } else if (m.type === 'rec:end') {
            const meta = this.meta;
            if (meta) {
              const blob = new Blob(this.chunks, { type: meta.mime || 'application/octet-stream' });
              try { dc.send(JSON.stringify({ type: 'rec:ack', ok: true })); } catch { /* */ }
              this.onComplete(blob, meta);
            } else {
              try { dc.send(JSON.stringify({ type: 'rec:ack', ok: false })); } catch { /* */ }
            }
            setTimeout(() => this.cleanup(), 600);
          }
        } catch { /* */ }
      } else {
        const buf: ArrayBuffer = ev.data instanceof ArrayBuffer ? ev.data : await (ev.data as Blob).arrayBuffer();
        this.chunks.push(buf); this.received += buf.byteLength;
        if (this.meta?.size) this.onProgress?.(Math.min(100, Math.round((this.received / this.meta.size) * 100)));
      }
    };
  }

  cleanup(): void {
    try { this.dc?.close(); } catch { /* */ }
    try { this.pc?.close(); } catch { /* */ }
    this.pc = null; this.dc = null;
  }
}
